import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useClient } from '../providers/supabase'
import { MfaChallenge } from './mfaChallenge'
import { NotFound } from './notFound'
import { SplashScreen } from './splashScreen'

/**
 * Query values Laravel puts on `/signIn/email` when a backend account
 * (`reimbursement-tracker-admin.users`) cannot open the admin app yet.
 * Keep these identical to `App\Auth\MfaAssurance::ADMIN_*`.
 */
export const ADMIN_MFA_ENROLLMENT_REQUIRED = 'mfa_enrollment_required'
export const ADMIN_MFA_STEP_UP_REQUIRED = 'mfa_step_up_required'

type AdminMfaPhase = 'loading' | 'error' | 'blocked' | 'enroll' | 'step-up' | 'continue' | 'not-found'

/**
 * Set just before an automatic continue. Laravel decides from the `auth_token`
 * cookie, which can lag the in-memory session. If that refusal sends the
 * browser straight back, a second automatic continue would bounce forever.
 */
const ADMIN_MFA_AUTO_CONTINUE_KEY = 'admin-mfa-auto-continue'
const AUTO_CONTINUE_WINDOW_MS = 30_000

export function rememberAdminMfaAutoContinue(returnTo: string, at = Date.now()) {
  try {
    sessionStorage.setItem(ADMIN_MFA_AUTO_CONTINUE_KEY, JSON.stringify({ returnTo, at }))
  } catch {
    // Loop protection is best-effort when storage is blocked.
  }
}

export function clearAdminMfaAutoContinue() {
  try {
    sessionStorage.removeItem(ADMIN_MFA_AUTO_CONTINUE_KEY)
  } catch {
    // Same as above: a blocked storage cannot record the continue either.
  }
}

export function adminMfaAutoContinueJustBounced(now = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(ADMIN_MFA_AUTO_CONTINUE_KEY)
    if (!raw) return false
    const parsed = JSON.parse(raw) as { at?: unknown }
    return typeof parsed.at === 'number' && now - parsed.at >= 0 && now - parsed.at < AUTO_CONTINUE_WINDOW_MS
  } catch {
    return false
  }
}

/**
 * Admin return targets are same-origin paths under `/admin`. Anything else
 * falls back to the admin home so a crafted `returnTo` cannot leave the app.
 */
export function safeAdminReturnTo(value: string | null | undefined): string {
  if (!value || value.includes('\\') || value.includes('..') || value.includes('://')) {
    return '/admin'
  }
  const path = value.split('?')[0]
  if (path.includes('//') || (path !== '/admin' && !path.startsWith('/admin/'))) {
    return '/admin'
  }
  return value
}

export function decideAdminMfaPhase(input: {
  reason: string | null
  currentLevel: string | null | undefined
  hasVerifiedFactor: boolean
}): Exclude<AdminMfaPhase, 'loading' | 'error' | 'blocked'> {
  const known = input.reason === ADMIN_MFA_ENROLLMENT_REQUIRED
    || input.reason === ADMIN_MFA_STEP_UP_REQUIRED
  if (!known) {
    return 'not-found'
  }
  if (input.currentLevel === 'aal2' && input.hasVerifiedFactor) {
    return 'continue'
  }
  if (input.hasVerifiedFactor) {
    return 'step-up'
  }
  return 'enroll'
}

/**
 * Shown when the admin app sends a signed-in backend account back because
 * the session is not aal2 with a verified factor. An account that still
 * needs to enroll sees the requirement. An account that already has an
 * authenticator sees the existing step-up prompt.
 */
export function AdminMfaRedirect() {
  const [params] = useSearchParams()
  const reason = params.get('reason')
  const returnTo = safeAdminReturnTo(params.get('returnTo'))
  const client = useClient()
  const [phase, setPhase] = useState<AdminMfaPhase>('loading')
  const [attempt, setAttempt] = useState(0)

  const stopContinuing = useCallback(() => {
    setPhase('error')
  }, [])

  // A code the member just entered is a new attempt. Clear the bounce marker
  // so that success can navigate once.
  const continueToAdmin = useCallback(() => {
    clearAdminMfaAutoContinue()
    setPhase('continue')
  }, [])

  useEffect(() => {
    if (reason !== ADMIN_MFA_ENROLLMENT_REQUIRED && reason !== ADMIN_MFA_STEP_UP_REQUIRED) {
      setPhase('not-found')
      return
    }

    let cancelled = false
    setPhase('loading')
    Promise.all([
      client.auth.mfa.getAuthenticatorAssuranceLevel(),
      client.auth.mfa.listFactors(),
    ]).then(([aal, factors]) => {
      if (cancelled) { return }
      const totp = factors.data?.totp
      if (aal.error || factors.error || !Array.isArray(totp)) {
        setPhase('error')
        return
      }
      const next = decideAdminMfaPhase({
        reason,
        currentLevel: aal.data?.currentLevel,
        hasVerifiedFactor: totp.some((factor) => factor.status === 'verified'),
      })
      if (next === 'continue' && adminMfaAutoContinueJustBounced()) {
        setPhase('blocked')
        return
      }
      setPhase(next)
    }).catch(() => {
      if (!cancelled) { setPhase('error') }
    })

    return () => { cancelled = true }
  }, [client, reason, attempt])

  if (phase === 'not-found') {
    return <NotFound />
  }
  if (phase === 'error') {
    return <div className="row mt-4 justify-content-center">
      <div className="col-12 col-lg-6 text-center">
        <h1>Two-factor verification</h1>
        <p>We could not confirm your sign-in security. You stay here until that check succeeds.</p>
        <button type="button" className="btn btn-primary" onClick={() => {
          clearAdminMfaAutoContinue()
          setAttempt((n) => n + 1)
        }}>
          Try again
        </button>
      </div>
    </div>
  }
  if (phase === 'blocked') {
    return <div className="row mt-4 justify-content-center">
      <div className="col-12 col-lg-6 text-center">
        <h1>The admin app did not open</h1>
        <p>
          Your authenticator is verified, but the admin sign-in did not accept this
          session. You stay on this page. Try again when you want another attempt.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => {
          clearAdminMfaAutoContinue()
          setAttempt((n) => n + 1)
        }}>
          Try again
        </button>
      </div>
    </div>
  }
  if (phase === 'enroll') {
    return <AdminMfaEnrollmentRequired returnTo={returnTo} />
  }
  if (phase === 'step-up') {
    return <MfaChallenge onVerified={continueToAdmin} />
  }
  if (phase === 'continue') {
    return <ContinueToAdmin returnTo={returnTo} onFailed={stopContinuing} />
  }
  return <SplashScreen />
}

export function AdminMfaEnrollmentRequired({ returnTo }: { returnTo: string }) {
  return <div className="row mt-4 justify-content-center">
    <div className="col-12 col-lg-6">
      <h1>Plan administrators must turn on two-factor authentication</h1>
      <p>
        The admin app stays closed until this account has an authenticator set up
        and a code verified. After that, you continue to the admin page you were opening.
      </p>
      <Link
        className="btn btn-primary"
        to={{
          pathname: '/settings',
          search: `?returnTo=${encodeURIComponent(returnTo)}`,
          hash: '#two-factor',
        }}
      >
        Set up two-factor authentication
      </Link>
    </div>
  </div>
}

function ContinueToAdmin({ returnTo, onFailed }: { returnTo: string; onFailed: () => void }) {
  const client = useClient()

  useEffect(() => {
    let cancelled = false
    // refreshSession notifies onAuthStateChange before it resolves, and that
    // listener rewrites the /admin auth_token cookie. Navigating before that
    // leaves Laravel holding the previous aal claim.
    client.auth.refreshSession().then((result) => {
      if (cancelled) return
      if (result?.error) {
        onFailed()
        return
      }
      rememberAdminMfaAutoContinue(returnTo)
      window.location.assign(returnTo)
    }).catch(() => {
      if (!cancelled) onFailed()
    })
    return () => { cancelled = true }
  }, [client, onFailed, returnTo])

  return <div className="row mt-4 justify-content-center">
    <div className="col-12 col-lg-6 text-center">
      <h1>Opening the admin app</h1>
      <p>Your authenticator is verified. Continuing to the admin page you were opening.</p>
    </div>
  </div>
}
